import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiShieldCheck, HiXMark } from 'react-icons/hi2';
import { submitRegistration, verifyRegistrationOtp } from '../../services/settingsService';
import toast from 'react-hot-toast';

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const modalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
  },
};

export default function RegistrationOtpPrompt({ registrationData, open, onVerified, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const hasRequestedOtp = useRef(false);

  // Request OTP when component opens
  const prevOpenRef = useRef(false);

  const requestOtp = async () => {
    if (!registrationData || hasRequestedOtp.current) {
      return;
    }

    try {
      setInitializing(true);
      hasRequestedOtp.current = true;

      const fullName =
        `${registrationData.firstname || ''} ${registrationData.lastname || ''}`.trim();
      const result = await submitRegistration({
        fullName,
        email: registrationData.email,
        phone: registrationData.phone,
        program: registrationData.program || 'General',
        twoFactorEnabled: true,
        metadata: {
          businessStage: registrationData.businessStage,
          location: registrationData.location,
          organization: registrationData.organization,
        },
      });

      if (!result.requiresOtp) {
        // OTP not required - registration completed immediately
        setInitializing(false);
        if (onVerified) {
          await onVerified({ success: true, registrationId: result.registrationId });
        }
        return;
      }

      setRegistrationId(result.registrationId);
      setCode('');
      setError('');
      setInitializing(false);

      toast.success('OTP sent to your email. Please check your inbox.');
    } catch (e) {
      hasRequestedOtp.current = false;
      const errorMessage = e?.message || 'Unable to submit registration. Please try again.';
      toast.error(errorMessage);
      setError(errorMessage);
      setInitializing(false);
    }
  };

  // Request OTP when modal opens
  if (open && !prevOpenRef.current && registrationData) {
    requestOtp();
  }
  prevOpenRef.current = open;

  // Reset when closed
  if (!open && hasRequestedOtp.current) {
    hasRequestedOtp.current = false;
    setRegistrationId(null);
    setCode('');
    setError('');
    setInitializing(false);
  }

  if (!open) return null;

  const handleVerify = async e => {
    e.preventDefault();

    if (!registrationId || code.length !== 6) {
      setError('Please enter the 6-digit code sent to your email.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const res = await verifyRegistrationOtp({
        registrationId,
        email: registrationData.email,
        otp: code,
      });

      if (res.success) {
        toast.success('Registration verified successfully!');
        if (onVerified) {
          await onVerified({ success: true, registrationId });
        }
        setLoading(false);
      } else {
        const errorMsg = res.message || 'Invalid or expired OTP.';
        setError(errorMsg);
        toast.error(errorMsg);
        setLoading(false);
      }
    } catch (err) {
      // Handle specific error codes from backend
      let errorMessage = 'Invalid or expired OTP.';

      if (err?.code === 'deadline-exceeded' || err?.message?.includes('expired')) {
        errorMessage = 'OTP has expired. Please request a new code.';
      } else if (err?.code === 'unauthenticated' || err?.message?.includes('Invalid')) {
        errorMessage = 'Invalid OTP code. Please try again.';
      } else if (err?.code === 'failed-precondition') {
        errorMessage = err?.message || 'OTP verification not required or already completed.';
      } else if (err?.code === 'not-found') {
        errorMessage = 'Registration not found. Please start over.';
      } else if (err?.code === 'permission-denied') {
        errorMessage = 'Email does not match registration.';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!registrationData) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      hasRequestedOtp.current = false; // Allow resend

      const fullName =
        `${registrationData.firstname || ''} ${registrationData.lastname || ''}`.trim();
      const result = await submitRegistration({
        fullName,
        email: registrationData.email,
        phone: registrationData.phone,
        program: registrationData.program || 'General',
        twoFactorEnabled: true,
        metadata: {
          businessStage: registrationData.businessStage,
          location: registrationData.location,
          organization: registrationData.organization,
        },
      });

      if (!result.requiresOtp) {
        setLoading(false);
        if (onVerified) {
          await onVerified({ success: true, registrationId: result.registrationId });
        }
        return;
      }

      hasRequestedOtp.current = true;
      setRegistrationId(result.registrationId);
      setCode('');
      setError('');
      toast.success('A new OTP has been sent to your email.');
    } catch (e) {
      hasRequestedOtp.current = false;
      const errorMsg = e?.message || 'Failed to resend OTP. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && onCancel) onCancel();
  };

  return (
    <AnimatePresence>
      <motion.div
        key="otp-backdrop"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.15, ease: 'easeOut' }}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
      >
        <motion.div
          key="otp-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="relative w-[92%] max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:w-full"
        >
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <HiXMark className="h-5 w-5" />
          </button>

          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
              <HiShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Verify Registration</h2>
              <p className="text-xs text-gray-600">
                We sent a 6-digit verification code to{' '}
                <span className="font-medium">{registrationData?.email}</span>.
              </p>
            </div>
          </div>

          {initializing ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            </div>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 text-center text-2xl font-bold tracking-widest text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
                  placeholder="000000"
                />
                {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
                <p className="mt-2 text-xs text-gray-500">Code expires in 10 minutes.</p>
              </div>

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={loading}
                  className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                >
                  Resend code
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || code.length !== 6}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white shadow-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Verifying…' : 'Verify'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
